import React, { useState, useEffect, useMemo } from 'react';
import { FaCalculator, FaUndo, FaChartLine, FaExclamationCircle, FaShoppingBag } from 'react-icons/fa';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useFinance } from '../context/FinanceContext';

const ImpactCalculator = () => {
    const { stats, settings } = useFinance();
    const [spend, setSpend] = useState(0);
    const [income, setIncome] = useState(0);
    const [currentProfit, setCurrentProfit] = useState(0);
    const [impactedProfit, setImpactedProfit] = useState(0);
    const [target, setTarget] = useState(0);
    const [whatIfAmount, setWhatIfAmount] = useState(0);

    // Pre-fill from real data on mount
    useEffect(() => {
        if (stats.profit) setCurrentProfit(stats.profit);
        if (settings?.savingsTarget) setTarget(settings.savingsTarget);
    }, [stats.profit, settings?.savingsTarget]);

    useEffect(() => {
        setImpactedProfit(currentProfit - spend + income);
    }, [spend, income, currentProfit]);

    const progress = target > 0 ? (impactedProfit / target) * 100 : 0;

    // "What if I buy X?" calculations
    const whatIfAnalysis = useMemo(() => {
        if (whatIfAmount <= 0) return null;
        const monthlySpendCap = (settings?.shoppingCap || 0) + (settings?.lifestyleCap || 0);
        const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
        const currentDailyBudget = monthlySpendCap > 0 ? monthlySpendCap / daysInMonth : 0;
        const newDailyBudget = monthlySpendCap > 0 ? (monthlySpendCap - whatIfAmount) / daysInMonth : 0;
        const monthsToRecover = stats.profit > 0 ? Math.ceil(whatIfAmount / stats.profit) : 0;
        return { newDailyBudget: Math.max(0, newDailyBudget), currentDailyBudget, monthsToRecover };
    }, [whatIfAmount, settings, stats.profit]);

    // Projection chart: 6 months with vs without purchase
    const projectionData = useMemo(() => {
        const months = [];
        const monthlySavings = stats.profit > 0 ? stats.profit : 0;
        for (let i = 0; i <= 6; i++) {
            const d = new Date();
            d.setMonth(d.getMonth() + i);
            const label = d.toLocaleString('default', { month: 'short' });
            months.push({
                month: label,
                withoutPurchase: monthlySavings * i,
                withPurchase: Math.max(0, monthlySavings * i - whatIfAmount),
            });
        }
        return months;
    }, [stats.profit, whatIfAmount]);

    return (
        <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <p className="text-[10px] font-semibold text-accent-400 uppercase tracking-[0.2em] mb-1">Simulation</p>
                    <h1 className="text-2xl font-bold tracking-tight text-frost-900">Impact Calculator</h1>
                    <p className="text-frost-500 mt-0.5 text-sm">Simulate financial outcomes before taking decisions</p>
                </div>
                <button
                    onClick={() => { setSpend(0); setIncome(0); setCurrentProfit(stats.profit || 0); setTarget(settings?.savingsTarget || 0); setWhatIfAmount(0); }}
                    className="btn-pill bg-white/[0.03] border border-white/[0.06] text-frost-400 hover:text-frost-900 hover:bg-white/[0.06] flex items-center gap-3 text-xs"
                >
                    <FaUndo className="text-xs" /> Reset
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Controls */}
                <div className="glass-card p-10 space-y-10 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-accent-500/5 blur-[80px] rounded-full"></div>
                    <div className="flex items-center gap-5 relative z-10">
                        <div className="p-4 bg-accent-500/10 rounded-xl text-accent-400 border border-accent-500/20">
                            <FaCalculator className="text-xl" />
                        </div>
                        <h3 className="text-xl font-bold tracking-tight text-frost-900">Scenario Parameters</h3>
                    </div>

                    <div className="space-y-8 relative z-10">
                        <InputField label="Current Profit/Savings" value={currentProfit} onChange={setCurrentProfit} max={10000000} color="accent" />
                        <InputField label="Monthly Target" value={target} onChange={setTarget} max={10000000} color="accent" />
                        <div className="h-px bg-white/[0.06]"></div>
                        <SliderField label="Anticipated Spend" value={spend} onChange={setSpend} max={500000} color="gold" />
                        <SliderField label="Projected Income" value={income} onChange={setIncome} max={1000000} color="mint" />
                    </div>

                    {spend > 0 && (
                        <div className="p-6 bg-white/[0.03] border border-white/[0.06] rounded-xl relative z-10">
                            <h4 className="font-bold text-[10px] text-gold-400 mb-3 flex items-center gap-2 uppercase tracking-widest">
                                <FaExclamationCircle className="text-sm" /> Impact Analysis
                            </h4>
                            <p className="text-sm text-frost-800 font-medium leading-relaxed">
                                Spending ₹{spend.toLocaleString()} will require ₹{Math.ceil(spend / 30).toLocaleString()}/day additional liquidity over 30 days.
                            </p>
                        </div>
                    )}
                </div>

                {/* Impact View */}
                <div className="space-y-6">
                    <div className="glass-card p-10 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-accent-500/5 blur-[80px] rounded-full"></div>
                        <div className="absolute top-0 right-0 p-10 opacity-5 scale-150 rotate-12 transition-transform duration-1000 group-hover:rotate-45">
                            <FaChartLine className="text-[120px]" />
                        </div>

                        <div className="relative z-10">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-accent-400 mb-3">Projected Outcome</p>
                            <h2 className="text-6xl font-bold tracking-tight mb-10 gradient-text">
                                ₹{impactedProfit.toLocaleString()}
                            </h2>

                            {target > 0 && (
                                <div className="space-y-6">
                                    <div className="flex justify-between items-end">
                                        <p className="text-[10px] font-medium uppercase tracking-widest text-frost-400">Progress to Target</p>
                                        <p className="text-2xl font-bold text-accent-400 tracking-tight">{progress.toFixed(1)}%</p>
                                    </div>
                                    <div className="h-3 bg-white/[0.04] rounded-full overflow-hidden p-0.5">
                                        <div className="h-full bg-gradient-to-r from-accent-500 to-accent-400 rounded-full transition-all duration-1000 ease-out shadow-glow" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}></div>
                                    </div>
                                    <div className="flex justify-between text-[10px] font-medium text-frost-500">
                                        <span>Current: ₹{impactedProfit.toLocaleString()}</span>
                                        <span>Target: ₹{target.toLocaleString()}</span>
                                    </div>
                                </div>
                            )}

                            {target === 0 && <p className="text-sm text-frost-500 text-center py-4">Set a target above to see progress</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <ImpactMetric label="Time to Target"
                            value={target > 0 && income > 0 ? Math.ceil(Math.max(0, target - impactedProfit) / income) : '—'}
                            unit={target > 0 && income > 0 ? 'Months' : ''} subtitle="at simulated rate" />
                        <ImpactMetric label="Daily Need"
                            value={spend > 0 ? `₹${Math.ceil(spend / 30).toLocaleString()}` : '—'}
                            unit={spend > 0 ? '/day' : ''} subtitle="to cover spend" />
                    </div>
                </div>
            </div>

            {/* What if I buy X? */}
            <div className="glass-card p-8 space-y-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-gold-500/5 blur-[80px] rounded-full"></div>
                <div className="flex items-center gap-5 relative z-10">
                    <div className="p-4 bg-gold-500/10 rounded-xl text-gold-400 border border-gold-500/20">
                        <FaShoppingBag className="text-xl" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold tracking-tight text-frost-900">What if I buy X?</h3>
                        <p className="text-sm text-frost-500 mt-0.5">See how a purchase impacts your budget</p>
                    </div>
                </div>
                <div className="relative z-10">
                    <InputField label="Purchase Amount" value={whatIfAmount} onChange={setWhatIfAmount} max={10000000} color="gold" />
                </div>
                {whatIfAnalysis && (
                    <div className="grid grid-cols-3 gap-4 relative z-10">
                        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 text-center">
                            <p className="text-[10px] text-frost-400 uppercase tracking-widest mb-2">New Daily Budget</p>
                            <p className="text-2xl font-bold text-frost-900">₹{whatIfAnalysis.newDailyBudget.toFixed(0)}</p>
                            <p className="text-[10px] text-frost-400 mt-1">was ₹{whatIfAnalysis.currentDailyBudget.toFixed(0)}</p>
                        </div>
                        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 text-center">
                            <p className="text-[10px] text-frost-400 uppercase tracking-widest mb-2">Months to Recover</p>
                            <p className="text-2xl font-bold text-gold-400">{whatIfAnalysis.monthsToRecover || '—'}</p>
                            <p className="text-[10px] text-frost-400 mt-1">at current savings rate</p>
                        </div>
                        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 text-center">
                            <p className="text-[10px] text-frost-400 uppercase tracking-widest mb-2">Budget Impact</p>
                            <p className={`text-2xl font-bold ${whatIfAnalysis.newDailyBudget > 0 ? 'text-mint-400' : 'text-gold-400'}`}>
                                {whatIfAnalysis.newDailyBudget > 0 ? 'Viable' : 'Risky'}
                            </p>
                            <p className="text-[10px] text-frost-400 mt-1">{whatIfAnalysis.newDailyBudget > 0 ? 'within budget' : 'exceeds budget'}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Projection Chart */}
            {whatIfAmount > 0 && (
                <div className="glass-card p-6">
                    <h3 className="text-base font-bold mb-4 text-frost-900">Savings Trajectory (Next 6 Months)</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={projectionData}>
                            <defs>
                                <linearGradient id="withoutGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="withGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#fbbf24" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#e2e8f0' }} />
                            <Area type="monotone" dataKey="withoutPurchase" stroke="#34d399" fill="url(#withoutGrad)" strokeWidth={2} name="Without Purchase" />
                            <Area type="monotone" dataKey="withPurchase" stroke="#fbbf24" fill="url(#withGrad)" strokeWidth={2} name="With Purchase" />
                        </AreaChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-6 mt-3 justify-center">
                        <div className="flex items-center gap-2 text-xs text-frost-400"><div className="w-3 h-1 bg-mint-400 rounded"></div> Without Purchase</div>
                        <div className="flex items-center gap-2 text-xs text-frost-400"><div className="w-3 h-1 bg-gold-400 rounded"></div> With Purchase</div>
                    </div>
                </div>
            )}
        </div>
    );
};

const InputField = ({ label, value, onChange, max, color }) => (
    <div className="space-y-2">
        <div className="flex justify-between items-end">
            <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">{label}</label>
            <span className={`text-lg font-bold tracking-tight ${color === 'accent' ? 'text-accent-400' : color === 'mint' ? 'text-mint-400' : 'text-gold-400'}`}>₹{value.toLocaleString()}</span>
        </div>
        <input
            type="number" value={value || ''}
            onChange={(e) => onChange(Math.min(max, Math.max(0, parseInt(e.target.value) || 0)))}
            placeholder="Enter amount"
            className="w-full px-5 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-sm font-medium text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500/30 placeholder:text-frost-400 transition-all"
        />
    </div>
);

const SliderField = ({ label, value, onChange, max, color }) => (
    <div className="space-y-3">
        <div className="flex justify-between items-end">
            <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">{label}</label>
            <span className={`text-2xl font-bold tracking-tight ${color === 'mint' ? 'text-mint-400' : 'text-gold-400'}`}>₹{value.toLocaleString()}</span>
        </div>
        <input
            type="range" min="0" max={max} step="500" value={value}
            onChange={(e) => onChange(parseInt(e.target.value))}
            className="w-full"
        />
    </div>
);

const ImpactMetric = ({ label, value, unit, subtitle }) => (
    <div className="glass-card p-8 flex flex-col justify-between group relative overflow-hidden">
        <div className="absolute top-0 right-0 w-20 h-20 bg-accent-500/5 blur-3xl rounded-full"></div>
        <p className="text-[10px] font-medium text-frost-400 uppercase tracking-widest relative z-10">{label}</p>
        <div className="mt-6 flex items-end gap-2 relative z-10">
            <span className="text-3xl font-bold text-frost-900 tracking-tight group-hover:text-accent-300 transition-colors">{value}</span>
            <span className="text-[10px] font-medium text-frost-400 mb-1 uppercase tracking-widest">{unit}</span>
        </div>
        <p className="text-[10px] text-accent-400 font-medium mt-3 uppercase tracking-wider border-t border-white/[0.06] pt-3 relative z-10">{subtitle}</p>
    </div>
);

export default ImpactCalculator;
